import { Signal } from '@angular/core';
import { Skill } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_SKILLS = 25;

export interface SkillsAiPageContextSignals {
  readonly skills: Signal<Skill[]>;
  readonly searchQuery: Signal<string>;
  readonly sortField: Signal<string | null>;
  readonly sortOrder: Signal<number>;
  readonly originalSkill: Signal<Skill | null>;
  readonly isEditDialogVisible: Signal<boolean>;
}

/**
 * Registers AI assistant page context for /settings/skills.
 *
 * - `entity` = the skill being edited (dialog open). Create dialog leaves
 *   `entity` undefined.
 * - `visible_rows` = first 25 skills.
 * - `filters` = search and sort.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideSkillsAiPageContext(state: SkillsAiPageContextSignals): void {
  providePageContext(() => {
    const editing = state.isEditDialogVisible() ? state.originalSkill() : null;
    const sortField = state.sortField();
    const sortOrder = state.sortOrder();
    const search = state.searchQuery().trim();

    return {
      entity: editing
        ? {
            type: 'skill',
            id: editing.id,
            label: editing.name,
          }
        : undefined,
      visible_rows: state
        .skills()
        .slice(0, MAX_VISIBLE_SKILLS)
        .map((skill) => ({
          type: 'skill',
          id: skill.id,
          label: skill.name,
        })),
      filters: {
        ...(search.length > 0 && { search }),
        ...(sortField &&
          sortOrder !== 0 && {
            sort: `${sortField}:${sortOrder === 1 ? 'asc' : 'desc'}`,
          }),
      },
    };
  });
}
